import { useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  Collapse,
  Grid,
  InputBase,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';

import inputStyles from './inputStyles';
import { isLeap } from '../../library/utils';

const monthsOptionsData = [
  {
    display: 'January',
    value: 0,
    days: 31,
  },
  {
    display: 'February',
    value: 1,
    days: 28,
  },
  {
    display: 'March',
    value: 2,
    days: 31,
  },
  {
    display: 'April',
    value: 3,
    days: 30,
  },
  {
    display: 'May',
    value: 4,
    days: 31,
  },
  {
    display: 'June',
    value: 5,
    days: 30,
  },
  {
    display: 'July',
    value: 6,
    days: 31,
  },
  {
    display: 'August',
    value: 7,
    days: 31,
  },
  {
    display: 'September',
    value: 8,
    days: 30,
  },
  {
    display: 'October',
    value: 9,
    days: 31,
  },
  {
    display: 'November',
    value: 10,
    days: 30,
  },
  {
    display: 'December',
    value: 11,
    days: 31,
  },
];

const StyledDateSelect = (props) => {
  const {
    id,
    label,
    value,
    defaultValue,
    minDate,
    maxDate,
    handleChange,
    selectClasses,
    selectNoPaddingLeft,
    listItemTextTypographyStyle,
    noLabel,
    boxComponent,
    boxSx,
    noBottomMargin,
    listItemTextSx,
    endComponent,
    required,
    error,
    errorMessage,
    helperText,
    isLoading,
    touched,
    disabled,
  } = props;
  const styles = inputStyles();

  const [onBlur, setOnBlur] = useState(!!touched);
  const [selectedYear, setSelectedYear] = useState(
    defaultValue ? defaultValue.getFullYear() : 'select'
  );
  const [selectedMonth, setSelectedMonth] = useState(
    defaultValue ? defaultValue.getMonth() : 'select'
  );
  const [selectedDay, setSelectedDay] = useState(
    defaultValue ? defaultValue.getDate() : 'select'
  );
  const [daysOptions, setDaysOptions] = useState([]);
  const [monthsOptions, setMonthsOptions] = useState([]);
  const [yearsOptions, setYearsOptions] = useState([]);

  useEffect(() => {
    setOnBlur(!!touched);
  }, [touched]);

  useEffect(() => {
    if (value) {
      setSelectedYear(value.getFullYear());
      setSelectedMonth(value.getMonth());
      setSelectedDay(value.getDate());
    }
  }, [value]);

  useEffect(() => {
    const dateToday = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );
    const tempYears = [];
    let tempMinYear = dateToday.getFullYear() - 100;
    let tempMaxYear = dateToday.getFullYear() + 101;

    if (minDate) {
      tempMinYear = minDate.getFullYear();
    }

    if (maxDate) {
      tempMaxYear = maxDate.getFullYear() + 1;
    }

    for (let index = tempMinYear; index < tempMaxYear; index++) {
      tempYears.push({
        display: index.toString(),
        value: index,
      });
    }
    setYearsOptions(tempYears);
  }, [maxDate, minDate]);

  useEffect(() => {
    const tempMonths = [];
    let tempMinMonth = 0;
    let tempMaxMonth = 12;

    if (
      selectedYear !== 'select' &&
      minDate &&
      minDate.getFullYear() === selectedYear
    ) {
      tempMinMonth = minDate.getMonth();
    }

    if (
      selectedYear !== 'select' &&
      maxDate &&
      maxDate.getFullYear() === selectedYear
    ) {
      tempMaxMonth = maxDate.getMonth() + 1;
    }

    for (let index = tempMinMonth; index < tempMaxMonth; index++) {
      tempMonths.push({
        display: monthsOptionsData[index].display,
        value: index,
      });
    }

    setMonthsOptions(tempMonths);
  }, [maxDate, minDate, selectedYear]);

  useEffect(() => {
    const tempDays = [];
    let tempMinDay = 1;
    let tempMaxDay =
      selectedMonth === 'select'
        ? 32
        : monthsOptionsData.find((m) => m.value === selectedMonth).days + 1;

    if (
      selectedYear !== 'select' &&
      selectedMonth !== 'select' &&
      minDate &&
      minDate.getFullYear() === selectedYear &&
      minDate.getMonth() === selectedMonth
    ) {
      tempMinDay = minDate.getDate();
    }

    if (
      selectedYear !== 'select' &&
      selectedMonth === 1 &&
      isLeap(selectedYear)
    ) {
      tempMaxDay = 30;
    }

    if (
      selectedYear !== 'select' &&
      selectedMonth !== 'select' &&
      maxDate &&
      maxDate.getFullYear() === selectedYear &&
      maxDate.getMonth() === selectedMonth
    ) {
      tempMaxDay = maxDate.getDate() + 1;
    }

    for (let index = tempMinDay; index < tempMaxDay; index++) {
      tempDays.push({
        display: index.toString(),
        value: index,
      });
    }
    setDaysOptions(tempDays);
  }, [maxDate, minDate, selectedMonth, selectedYear]);

  const handleSelectChange = (mode, e) => {
    const dateToday = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );
    let minMonth = 0;
    let maxMonth = 11;
    let returnMonth = 0;
    let minDays = 1;
    let maxDays = 31;

    if (mode === 'month') {
      maxDays = monthsOptionsData.find((m) => m.value === e.target.value).days;
    }

    if (
      mode === 'year' &&
      minDate &&
      minDate.getFullYear() === e.target.value
    ) {
      minMonth = minDate.getMonth();
    }

    if (
      mode === 'year' &&
      maxDate &&
      maxDate.getFullYear() === e.target.value
    ) {
      maxMonth = maxDate.getMonth();
    }

    returnMonth =
      selectedMonth === 'select'
        ? minMonth
        : maxMonth < selectedMonth
        ? maxMonth
        : minMonth > selectedMonth
        ? minMonth
        : selectedMonth;

    if (
      mode === 'year' &&
      minDate &&
      minDate.getFullYear() === e.target.value &&
      minDate.getMonth() === returnMonth
    ) {
      minDays = minDate.getDate();
    }

    if (
      selectedYear !== 'select' &&
      selectedMonth !== 'select' &&
      mode === 'month' &&
      maxDate &&
      maxDate.getFullYear() === selectedYear &&
      maxDate.getMonth() === selectedMonth
    ) {
      maxDays = maxDate.getDate();
    }

    let tempMinYear = dateToday.getFullYear() - 100;
    if (minDate) {
      tempMinYear = minDate.getFullYear();
    }

    switch (mode) {
      case 'year':
        handleChange(
          new Date(
            e.target.value,
            returnMonth,
            selectedDay === 'select'
              ? minDays
              : maxDays < selectedDay
              ? maxDays
              : minDays > selectedDay
              ? minDays
              : selectedDay
          )
        );
        break;
      case 'month':
        handleChange(
          new Date(
            selectedYear === 'select'
              ? dateToday.getMonth() > e.target.value
                ? tempMinYear + 1
                : tempMinYear
              : selectedYear,
            e.target.value,
            selectedYear === 'select'
              ? dateToday.getMonth() > e.target.value
                ? 1
                : selectedDay === 'select'
                ? minDays
                : maxDays < selectedDay
                ? maxDays
                : selectedDay
              : selectedDay === 'select'
              ? minDays
              : maxDays < selectedDay
              ? maxDays
              : selectedDay
          )
        );
        break;
      case 'day':
        handleChange(
          new Date(
            selectedYear === 'select' ? tempMinYear : selectedYear,
            selectedMonth === 'select' ? minMonth : selectedMonth,
            e.target.value
          )
        );
        break;

      default:
        break;
    }
  };

  const selectComponent = (
    <Grid container direction="row">
      {yearsOptions.length > 0 && (
        <Select
          classes={
            selectClasses || {
              select: selectNoPaddingLeft
                ? styles.selectRootNoPaddingLeft
                : styles.selectRoot,
            }
          }
          sx={{ width: '30%', backgroundColor: '#FFF', mr: '1%' }}
          input={<InputBase classes={{ root: styles.selectInputBase }} />}
          value={selectedYear}
          onChange={handleSelectChange.bind(this, 'year')}
          onClose={() => setOnBlur(true)}
          disabled={disabled}
        >
          {selectedYear === 'select' && (
            <MenuItem key={id + '-year--1'} value="select">
              <ListItemText
                primary={
                  <Typography style={listItemTextTypographyStyle}>
                    Year
                  </Typography>
                }
                sx={listItemTextSx}
              />
            </MenuItem>
          )}
          {yearsOptions.map((o, index) => (
            <MenuItem key={id + '-year-' + index} value={o.value}>
              <ListItemText
                primary={
                  <Typography style={listItemTextTypographyStyle}>
                    {o.display}
                  </Typography>
                }
                sx={listItemTextSx}
              />
            </MenuItem>
          ))}
        </Select>
      )}
      {monthsOptions.length > 0 && (
        <Select
          classes={
            selectClasses || {
              select: selectNoPaddingLeft
                ? styles.selectRootNoPaddingLeft
                : styles.selectRoot,
            }
          }
          sx={{ width: '48%', backgroundColor: '#FFF', mr: '1%' }}
          input={<InputBase classes={{ root: styles.selectInputBase }} />}
          value={selectedMonth}
          onChange={handleSelectChange.bind(this, 'month')}
          onClose={() => setOnBlur(true)}
          disabled={disabled}
        >
          {selectedMonth === 'select' && (
            <MenuItem key={id + '-month--1'} value="select">
              <ListItemText
                primary={
                  <Typography style={listItemTextTypographyStyle}>
                    Month
                  </Typography>
                }
                sx={listItemTextSx}
              />
            </MenuItem>
          )}
          {monthsOptions.map((o, index) => (
            <MenuItem key={id + '-month-' + index} value={o.value}>
              <ListItemText
                primary={
                  <Typography style={listItemTextTypographyStyle}>
                    {o.display}
                  </Typography>
                }
                sx={listItemTextSx}
              />
            </MenuItem>
          ))}
        </Select>
      )}
      {daysOptions.length > 0 && (
        <Select
          classes={
            selectClasses || {
              select: selectNoPaddingLeft
                ? styles.selectRootNoPaddingLeft
                : styles.selectRoot,
            }
          }
          sx={{ width: '20%', backgroundColor: '#FFF' }}
          input={<InputBase classes={{ root: styles.selectInputBase }} />}
          value={selectedDay}
          onChange={handleSelectChange.bind(this, 'day')}
          onClose={() => setOnBlur(true)}
          disabled={disabled}
        >
          {selectedDay === 'select' && (
            <MenuItem key={id + '-day--1'} value="select">
              <ListItemText
                primary={
                  <Typography style={listItemTextTypographyStyle}>
                    Day
                  </Typography>
                }
                sx={listItemTextSx}
              />
            </MenuItem>
          )}
          {selectedYear !== 'select' &&
            selectedMonth !== 'select' &&
            selectedDay !== 'select' &&
            daysOptions.map((o, index) => (
              <MenuItem key={id + '-day-' + index} value={o.value}>
                <ListItemText
                  primary={
                    <Typography style={listItemTextTypographyStyle}>
                      {o.display}
                    </Typography>
                  }
                  sx={listItemTextSx}
                />
              </MenuItem>
            ))}
        </Select>
      )}
    </Grid>
  );

  return (
    <Box component={boxComponent} sx={boxSx} mb={noBottomMargin ? 0 : 1}>
      {!noLabel && (
        <InputLabel
          required={required}
          sx={{
            '& .MuiInputLabel-asterisk': {
              color: 'red',
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <div style={{ position: 'relative', backgroundColor: 'none' }}>
        <Grid
          container
          direction="row"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
        >
          <div style={{ flexGrow: 1 }}>
            {/* {noFormControl ? (
              selectComponent
            ) : (
              <FormControl
                fullWidth={fullWidth}
                variant={formControlVariant}
                className={formControlClassName || styles.formControlClass}
              >
                {selectComponent}
              </FormControl>
            )} */}
            {selectComponent}
          </div>
          {endComponent}
        </Grid>
        {helperText && (
          <InputLabel>
            <Typography fontSize="0.7rem">{helperText}</Typography>
          </InputLabel>
        )}
        <Collapse in={error || (required && !value && onBlur)}>
          <InputLabel>
            <Typography fontSize="0.7rem" color="red">
              {errorMessage ||
                (required && !value && onBlur && `${label} is required.`)}
            </Typography>
          </InputLabel>
        </Collapse>
        <StyledEngineProvider injectFirst>
          <Backdrop
            classes={{ root: styles.backdrop }}
            style={{ position: 'absolute', backgroundColor: 'transparent' }}
            open={isLoading || false}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </StyledEngineProvider>
      </div>
    </Box>
  );
};

export default StyledDateSelect;
